/** Brand colour themes. Each theme is a palette for the four logo colour
 * classes the generator (scripts/build_brand.py) baked into logo.svg / mark.svg:
 *   base   — the textured card field   (--logo-base)
 *   gold   — brushed monogram + bar     (--logo-gold)
 *   white  — wordmark whites            (--logo-white; transparent on the mark)
 *   accent — the green sparkle          (--logo-accent)
 *   grain  — the texture highlight tint (--logo-grain)
 * Switching a theme just rewrites these CSS variables; the inline SVG animates
 * the swap via a `fill` transition, and the favicon is re-rasterised. */
export interface LogoTheme {
  id: string;
  label: string;
  base: string;
  gold: string;
  white: string;
  accent: string;
  grain: string;
}

export const LOGO_THEMES: LogoTheme[] = [
  // Obsidian — the shipped default: neutral near-black surfaces + a violet
  // accent. Its `gold` IS the studio's `--primary` violet (#8473de →
  // 250 62% 66%), so the whole app (both token systems) reads violet.
  { id: "obsidian", label: "Obsidian", base: "#0d0d0f", gold: "#8473de", white: "#e7e5da", accent: "#7ed957", grain: "#ffffff" },
  // The artwork's own colours — brass gold on green-ink.
  { id: "brass", label: "Brass", base: "#0c0f0c", gold: "#dfb15f", white: "#e7e5da", accent: "#7ed957", grain: "#ffffff" },
  // The app's "Observatory Ink & Brass" palette.
  { id: "observatory", label: "Observatory", base: "#0b0e0c", gold: "#e0b15c", white: "#e7e5da", accent: "#5fae8b", grain: "#ffffff" },
  // Cool instrument blue.
  { id: "nocturne", label: "Nocturne", base: "#0b0f16", gold: "#9bbcd9", white: "#e6edf5", accent: "#7fb4d8", grain: "#ffffff" },
  // Deep orchid.
  { id: "orchid", label: "Orchid", base: "#140f1d", gold: "#cBa3e6", white: "#efe9f5", accent: "#bc8fd9", grain: "#ffffff" },
  // Warm ember.
  { id: "ember", label: "Ember", base: "#160d0b", gold: "#e7a25c", white: "#f1e7df", accent: "#d9705f", grain: "#ffffff" },
];

export const DEFAULT_THEME_ID = "obsidian";

export function themeById(id: string | null | undefined): LogoTheme {
  return LOGO_THEMES.find((t) => t.id === id) ?? LOGO_THEMES[0]!;
}

/** The `--logo-*` custom properties for a theme, applied to :root. */
export function logoVars(t: LogoTheme): Record<string, string> {
  return {
    "--logo-base": t.base,
    "--logo-gold": t.gold,
    "--logo-white": t.white,
    "--logo-accent": t.accent,
    "--logo-grain": t.grain,
  };
}

// --- whole-APP palette derived from a theme --------------------------------
// The studio's Tailwind colours (ink / line / paper / brass) are driven by
// `rgb(var(--gv-*) / <alpha-value>)` channel variables (see tailwind.config.ts).
// Overriding the `--gv-*` triples on :root re-themes the ENTIRE app — every
// background, text colour, hairline, and brass accent — not just the logo.

function toRgb(hex: string): [number, number, number] {
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const clampByte = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
/** "r g b" channel string for `rgb(var(--x) / <alpha>)`. */
function channels(hex: string): string {
  const [r, g, b] = toRgb(hex);
  return `${r} ${g} ${b}`;
}
/** Mix `t` (0..1) of `b` into `a`, returned as an "r g b" channel string. */
function mixCh(a: string, b: string, t: number): string {
  const [ar, ag, ab] = toRgb(a);
  const [br, bg, bb] = toRgb(b);
  return `${clampByte(ar + (br - ar) * t)} ${clampByte(ag + (bg - ag) * t)} ${clampByte(ab + (bb - ab) * t)}`;
}
const BLACK = "#000000";
const WHITE = "#ffffff";

/** Every `--gv-*` channel variable for a theme, derived coherently from its
 * base/gold/white so every legacy `rgb(var(--gv-*))` surface recolours. The
 * default (obsidian) yields neutral near-black surfaces + a violet brass slot;
 * brass/observatory land within ~1 channel of the shipped Observatory palette
 * (the :root fallback in styles.css holds those exact values for first paint). */
export function appThemeVars(t: LogoTheme): Record<string, string> {
  const { base, gold, white } = t;
  return {
    "--gv-ink": channels(base),
    "--gv-ink-raised": mixCh(base, WHITE, 0.045),
    "--gv-ink-overlay": mixCh(base, WHITE, 0.095),
    "--gv-ink-hover": mixCh(base, WHITE, 0.14),
    "--gv-line": mixCh(base, WHITE, 0.13),
    "--gv-line-strong": mixCh(base, WHITE, 0.24),
    "--gv-paper": channels(white),
    "--gv-paper-dim": mixCh(white, base, 0.34),
    "--gv-paper-faint": mixCh(white, base, 0.52),
    "--gv-brass": channels(gold),
    "--gv-brass-bright": mixCh(gold, WHITE, 0.32),
    "--gv-brass-ink": mixCh(gold, BLACK, 0.84),
    "--gv-brass-dim": mixCh(gold, base, 0.42),
  };
}

// --- shadcn/ui obsidian tokens derived from a theme -------------------------
// The shell chrome + shared primitives read the shadcn HSL *channel* variables
// (`hsl(var(--primary))`, `hsl(var(--background))`, …). Each palette now writes
// them too, so switching a palette recolours the primary accent (chat launcher,
// buttons, checkboxes, progress, focus ring) and — because workspace.css maps
// `--wp-accent: hsl(var(--primary))` etc. — the whole dock, automatically.

/** hex → HSL (h∈[0,360), s/l∈[0,100]). */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const [r255, g255, b255] = toRgb(hex);
  const r = r255 / 255;
  const g = g255 / 255;
  const b = b255 / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

/** WCAG relative luminance (0..1) — picks a legible primary foreground. */
function relLuminance(hex: string): number {
  const lin = (c255: number) => {
    const c = c255 / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = toRgb(hex);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

// Obsidian is the neutral default: its shadcn tokens ARE the shipped values
// (violet --primary, zero-saturation surfaces) — matches :root in styles.css
// pixel-for-pixel, so the default theme never tints.
const OBSIDIAN_SHADCN: Record<string, string> = {
  "--primary": "250 62% 66%",
  "--primary-foreground": "0 0% 100%",
  "--ring": "250 62% 66%",
  "--background": "0 0% 12%",
  "--card": "0 0% 15%",
  "--popover": "0 0% 15%",
  "--secondary": "0 0% 20%",
  "--muted": "0 0% 18%",
  "--accent": "0 0% 22%",
  "--border": "0 0% 24%",
  "--input": "0 0% 20%",
  "--sidebar": "0 0% 10%",
};

// The surface lightness ladder, lifted verbatim from the obsidian defaults.
// A tinted palette KEEPS these L values and only bends the hue toward the
// palette's base + a restrained surface saturation — a tint, not a repaint.
const SURFACE_L: Record<string, number> = {
  "--background": 12,
  "--card": 15,
  "--popover": 15,
  "--secondary": 20,
  "--muted": 18,
  "--accent": 22,
  "--border": 24,
  "--input": 20,
  "--sidebar": 10,
};
const SURFACE_SAT = 12;

/** The shadcn HSL *channel* variables for a theme. Obsidian returns the exact
 * shipped defaults; every other palette derives `--primary` (+ a legible
 * foreground) from its `gold`, `--ring` = primary, and a subtle surface tint
 * from its `base` hue at the obsidian lightness ladder. */
export function appShadcnVars(t: LogoTheme): Record<string, string> {
  if (t.id === DEFAULT_THEME_ID) return { ...OBSIDIAN_SHADCN };
  const hue = hexToHsl(t.base).h;
  const p = hexToHsl(t.gold);
  const primary = `${p.h} ${p.s}% ${p.l}%`;
  const out: Record<string, string> = {
    "--primary": primary,
    // Light accents (golds, pastels) take a dark ink; a dark accent takes white.
    "--primary-foreground": relLuminance(t.gold) > 0.4 ? `${hue} 25% 10%` : "0 0% 100%",
    "--ring": primary,
  };
  for (const [key, l] of Object.entries(SURFACE_L)) {
    out[key] = `${hue} ${SURFACE_SAT}% ${l}%`;
  }
  return out;
}
